import { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { blobServiceClient, envVariables, userEmail } from "../../Common/Store/CommonSignals";
import { templateData } from "../../Template/NewTemplateSignals";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/bmp", "image/gif", "image/webp"];

// CosmosDB has a 2MB document limit. Reserve 1MB for the rest of the template JSON.
const MAX_BASE64_BYTES = 1 * 1024 * 1024;

async function uploadImageToBlob(file) {
  const tmpl = templateData.value;
  if (!tmpl?.id) {
    throw new Error("Template ID is missing — cannot upload image.");
  }
  const containerName = `${envVariables.value.REACT_APP_AZDEVOPS_BRANCH}-${tmpl.id}`
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 63);
  const containerClient = blobServiceClient.value.getContainerClient(containerName);
  await containerClient.createIfNotExists();

  const blobName = `${Date.now()}_${file.name}`;
  const blockBlobClient = containerClient.getBlockBlobClient(blobName);

  const arrayBuffer = await file.arrayBuffer();
  await blockBlobClient.uploadData(arrayBuffer, {
    metadata: {
      originalName: file.name,
      fileSize: file.size.toString(),
      fileType: file.type,
      templateId: tmpl.id,
      templateName: tmpl.templateName,
      templateCompany: tmpl.company,
      templateBranch: tmpl.branch,
      templateDepartment: tmpl.department,
      templateArea: tmpl.area,
      uploadedBy: userEmail.value,
    },
  });

  return blockBlobClient.url;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function getImageSrc(file) {
  if (blobServiceClient.value) {
    return await uploadImageToBlob(file);
  }

  const dataUrl = await readFileAsDataUrl(file);
  if (dataUrl.length > MAX_BASE64_BYTES) {
    const sizeMB = (dataUrl.length / (1024 * 1024)).toFixed(2);
    throw new Error(
      `Image too large for offline storage: ${sizeMB}MB as base64 (max 1MB). Use a smaller image or configure Azure Blob Storage.`
    );
  }
  return dataUrl;
}

function ImageSrcRenderer({ handlePropertyChange, currentPropertyValues, propName }) {
  const [imageSrc, setImageSrc] = useState(currentPropertyValues?.[propName] || "");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    setImageSrc(currentPropertyValues?.[propName] || "");
  }, [currentPropertyValues?.[propName]]);

  const handleUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ALLOWED_IMAGE_TYPES.join(",");
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        setIsUploading(true);
        setUploadError("");
        const src = await getImageSrc(file);
        setImageSrc(src);
        handlePropertyChange({ propName, propValue: src });
      } catch (err) {
        setUploadError(err.message);
        console.error("Error uploading image:", err);
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {imageSrc && (
        <img
          src={imageSrc}
          alt="Preview"
          style={{
            maxWidth: "100%",
            maxHeight: "150px",
            objectFit: "contain",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        />
      )}
      <Button
        type="button"
        onClick={handleUpload}
        themeColor="primary"
        disabled={isUploading}
      >
        {isUploading ? "Uploading..." : "Upload Image"}
      </Button>
      {uploadError && (
        <span style={{ color: "#d32f2f", fontSize: "12px" }}>{uploadError}</span>
      )}
      <Input
        name={propName}
        value={imageSrc}
        onChange={(e) => setImageSrc(e.target.value)}
        onBlur={() => handlePropertyChange({ propName, propValue: imageSrc })}
        placeholder="Or enter image URL..."
      />
    </div>
  );
}

export const ImageSrcProperty = {
  propertyName: "ImageSrc",
  propertyDisplay: "Image",
  propertyType: "string",
  propertyCategory: "Data",
  renderer: function ({ handlePropertyChange, currentPropertyValues }) {
    return (
      <ImageSrcRenderer
        handlePropertyChange={handlePropertyChange}
        currentPropertyValues={currentPropertyValues}
        propName={this.propertyName}
      />
    );
  },
};
